<template>
  <div class="family-list tongxin-layout">
    <h1 class="title-with-underline">請選擇家庭成員</h1>

    <div class="section mb-4">
      <h3 class="section-title">已授權</h3>
      <OptionList
        :items="authorizedProfiles"
        :selected="selectedProfileId"
        @update:selected="(value) => updateSelectedProfile(value, 'auth')"
      />
    </div>

    <div class="section">
      <h3 class="section-title">未授權</h3>
      <OptionList
        :items="unauthorizedProfiles"
        :selected="selectedProfileId"
        @update:selected="(value) => updateSelectedProfile(value, 'family')"
      />
    </div>

    <!-- 空狀態 -->
    <div class="empty-state" v-if="relations.length === 0 && !isLoading">
      <p>尚無親友資料</p>
    </div>

    <!-- 新增親友按鈕 -->
    <div v-if="isFromSetting" class="text-center">
      <button class="add-family" @click="goToAddMember">
        + 新增家庭成員
      </button>
    </div>

    <div style="padding-bottom: 96px"></div>
    <div class="fixed-footer-navigation">
      <TongxinNavigation
        :canProceed="selectedProfileId !== null"
        :backButtonIsVisible="true"
        :nextButtonIsVisible="true"
        textForNextStep="下一步"
        @back="handleBack"
        @next="handleNext"
      />
    </div>

    <!-- Loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <b-spinner style="border-width: 2px" />
    </div>
  </div>
</template>

<script>
import familyManager from '@/mixins/web/tongxin/familyManager'
import tongxinApi from '@/apis/web/tongxin'
import MemberCard from '@/components/web/proj/tongxin/family/MemberCard.vue'
import OptionList from '@/components/web/proj/tongxin/common/OptionList.vue';
import TongxinNavigation from '@/components/web/proj/tongxin/layout/TongxinNavigation.vue'

export default {
  name: 'TongxinFamilyList',
  mixins: [familyManager],
  components: {
    MemberCard,
    OptionList,
    TongxinNavigation,
  },
  data: () => ({
    selectedProfileId: null,
    selectedSource: null,
    selfCustomerInfo: null,
    isRetrievingSelfInfo: false,
  }),
  computed: {
    isFromSetting() {
      return this.$route.name === 'TongxinFamilyList'
    },
    isBookingFlow() {
      return this.$route.name === 'TongxinFamilySelection'
    },
    bookingReturnStep() {
      return this.$route.query.returnStep || 'confirmation'
    },
    bookingBackStep() {
      return this.$route.query.backStep || 'time-selection'
    },
    myProfile() {
      return {
        rowid: 'self',
        ptName: this.selfCustomerInfo?.name || '',
        relationship: '本人',
        ptIdno: this.selfCustomerInfo?.customer_code || '',
        ptBirthday: this.selfCustomerInfo?.birthday || null,
      }
    },
    authorizedProfiles() {
      return [this.myProfile, ...this.authRelations]
        .map(profile => ({
          label: profile.ptName,
          value: profile.rowid,
          description: profile.rowid === 'self' ? '本人' : null,
        }))
    },
    unauthorizedProfiles() {
      return this.familyRelations
        .map(profile => ({
          label: profile.ptName,
          value: profile.rowid,
        }))
    },
    isLoading() {
      return this.isRetrievingSelfInfo || this.isCallingFamilyApi
    },
  },
  async created() {
    await Promise.all([
      this.loadSelfProfile(),
      this.loadRelations(),
    ])
  },
  methods: {
    async loadSelfProfile() {
      try {
        this.isRetrievingSelfInfo = true
        const { data: res = {} } = await tongxinApi.getCustomerInfo()
        this.selfCustomerInfo = res
      } catch (error) {
        console.error('取得本人資料失敗:', error)
      } finally {
        this.isRetrievingSelfInfo = false
      }
    },

    updateSelectedProfile(profileId, source) {
      this.selectedProfileId = profileId
      this.selectedSource = source
      console.log(this.selectedProfileId, this.selectedSource)
    },

    handleBack() {
      if (this.isBookingFlow) {
        this.goBackToBooking(this.bookingBackStep)
        return
      }

      // is from setting
      this.$router.push({
        name: 'TongxinSetting',
        params: {
          org_code: this.$route.params.org_code || this.$route.query.org_code,
        },
      })
    },

    handleNext() {
      if (this.isBookingFlow) {
        const registrant = this.buildSelectedRegistrant()
        if (!registrant) return
        this.saveRegistrantToTongxinContext(registrant)
        this.goBackToBooking(this.bookingReturnStep)
        return
      }

      if (this.selectedProfileId === 'self') {
        this.goToMyProfile()
        return
      } else {
        const member = this.authRelations.find(m => m.rowid === this.selectedProfileId) || this.familyRelations.find(m => m.rowid === this.selectedProfileId)
        if (this.selectedSource === 'auth') {
          this.goToMemberProfile(member, this.selectedSource)
        } else {
          this.goToEditMember(member)
        }
      }
    },

    goToMyProfile() {
      this.$router.push({ name: 'TongxinFamilyMe' })
    },

    goToMemberProfile(member, source) {
      this.$router.push({
        name: 'TongxinFamilyMemberView',
        params: { memberId: member.rowid },
        query: { data: JSON.stringify(member), source },
      })
    },

    goToEditMember(member) {
      this.$router.push({
        name: 'TongxinFamilyMemberEdit',
        params: { memberId: member.rowid },
        query: { data: JSON.stringify(member) },
      })
    },

    goToAddMember() {
      this.$router.push({
        name: 'TongxinFamilyMemberAdd',
        query: { ...this.$route.query }
      })
    },

    goBackToBooking(stepId) {
      this.$router.push({
        name: 'TongxinStep',
        params: {
          org_code: this.$route.params.org_code || this.$route.query.org_code,
          stepId,
          categoryId: this.$route.query.categoryId || undefined,
        },
        query: { ...this.$route.query }
      })
    },

    buildSelectedRegistrant() {
      if (this.selectedProfileId === 'self') {
        return {
          rowid: 'self',
          source: 'self',
          name: this.selfCustomerInfo?.name || '',
          ssid: this.selfCustomerInfo?.customer_code || '',
        }
      }

      const authMember = this.authRelations.find(m => m.rowid === this.selectedProfileId)
      if (authMember) {
        return {
          rowid: authMember.rowid,
          source: 'auth',
          name: authMember.ptName || '',
          ssid: authMember.ptIdno || '',
        }
      }

      const familyMember = this.familyRelations.find(m => m.rowid === this.selectedProfileId)
      if (familyMember) {
        return {
          rowid: familyMember.rowid,
          source: 'family',
          name: familyMember.ptName || '',
          ssid: familyMember.ptIdno || '',
        }
      }

      return null
    },

    saveRegistrantToTongxinContext(registrant) {
      try {
        const raw = localStorage.getItem('tongxin-context')
        const parsed = raw ? JSON.parse(raw) : {}
        const context = parsed.context || {}
        context.registrant = registrant
        parsed.context = context
        localStorage.setItem('tongxin-context', JSON.stringify(parsed))
      } catch (error) {
        console.error('儲存掛號對象失敗:', error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.family-list {
  .section {
    .section-title {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 16px;
      text-align: center;
    }
  }

  .add-family {
    font-size: 18px;
    font-weight: bold;
    color: #207868;
    background: none;
    border: none;
  }

  .empty-state {
    text-align: center;
    padding: 40px 20px;
    color: #999;
    font-size: 16px;
  }
}
</style>
